/**
 * Domain extraction and validation utility
 * Supports raw domains, full URLs (http/https), subdomains, ports, paths, and IDN.
 */

/**
 * Extracts a clean, normalized domain name from any input (e.g. "https://www.example.com/page?x=1" -> "example.com")
 * @param {string} input 
 * @returns {string} Clean domain name
 */
export function extractCleanDomain(input) {
    if (!input || typeof input !== 'string') return '';
    let val = input.trim().toLowerCase();

    // Remove leading list markers or dashes (e.g. "- пример.com", "• domain.com")
    val = val.replace(/^[\s\-_•*–—]+\s*/, '');

    // Remove any protocol (http://, https://, ftp://, etc.)
    val = val.replace(/^[a-zA-Z][a-zA-Z0-9+.-]*:\/\//, '');

    // Remove user/password prefix if pasted (e.g. user:pass@host)
    if (val.includes('@')) {
        val = val.split('@').pop();
    }

    // Remove path, query parameters, and hash
    val = val.split('/')[0].split('?')[0].split('#')[0];

    // Remove port if present (e.g. host:3000)
    val = val.split(':')[0];

    // Remove leading 'www.'
    val = val.replace(/^www\./, '');

    // Clean leading/trailing dots and whitespace
    val = val.replace(/^\.+|\.+$/g, '').trim();

    return val;
}

/**
 * Validates whether the given string represents a valid domain name
 * Supports any valid domain format, including international/Cyrillic (e.g. "пример.com", "тест.рф")
 * @param {string} input 
 * @returns {boolean} True if input is a valid domain
 */
export function isValidDomainFormat(input) {
    const clean = extractCleanDomain(input);
    if (!clean || clean.length < 3 || clean.length > 253) return false;
    if (!clean.includes('.')) return false;
    if (clean.startsWith('.') || clean.endsWith('.')) return false;
    if (/\s/.test(clean)) return false;

    // Check each label format
    const labels = clean.split('.');
    if (labels.length < 2) return false;

    for (let i = 0; i < labels.length; i++) {
        const label = labels[i];
        if (!label || label.length > 63) return false;
        if (label.startsWith('-') || label.endsWith('-')) return false;
        // Supports Unicode letters (\p{L}), Unicode numbers (\p{N}), and hyphens
        if (!/^[\p{L}\p{N}-]+$/u.test(label)) return false;
    }

    // TLD must be at least 2 characters and not pure digits
    const tld = labels[labels.length - 1];
    if (tld.length < 2) return false;
    if (/^\d+$/.test(tld)) return false;

    return true;
}

/**
 * Calculates length-based default prices for auto-filling
 * @param {string} domain 
 * @param {object} minPrices 
 * @returns {{ clean: string, buyPrice: number, rentPrice: number, fwPrice: number }}
 */
export function calculateDomainLengthPrices(domain, minPrices = {}) {
    const clean = extractCleanDomain(domain) || 'brand.com';
    const sld = clean.split('.')[0] || 'brand';
    const len = Math.max(1, sld.length);

    let buyPrice = 1000;
    if (len === 1) buyPrice = 100000;
    else if (len === 2) buyPrice = 50000;
    else if (len === 3) buyPrice = 25000;
    else if (len === 4) buyPrice = 10000;
    else if (len === 5) buyPrice = 6000;
    else if (len === 6) buyPrice = 4000;
    else if (len === 7) buyPrice = 2500;
    else if (len === 8) buyPrice = 1500;
    else buyPrice = 1000;

    const minBuy = minPrices.buy || 100;
    const minRent = minPrices.rent || 50;
    const minFw = minPrices.forward || 10;

    buyPrice = Math.max(minBuy, buyPrice);
    const rentPrice = Math.max(minRent, Math.round((buyPrice * 0.05) / 10) * 10);
    const fwPrice = Math.max(minFw, Math.round(rentPrice / 30));

    return { clean, buyPrice, rentPrice, fwPrice };
}
